import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { AlertCircle, CheckCircle, Clock, MessageCircle, IndianRupee, ChevronDown, ChevronUp, X, Link } from 'lucide-react';
import { ClientPaymentLinkModal } from '../components/PaymentLinkModal';
import toast from 'react-hot-toast';

function fmt(n) { return '₹' + (Number(n) || 0).toLocaleString('en-IN'); }

const STATUS_MAP = {
  current: { cls: 'badge-green', label: '✓ Current' },
  overdue: { cls: 'badge-red', label: '⚠ Overdue' },
  paid: { cls: 'badge-green', label: '✓ Paid' },
  paid_late: { cls: 'badge-yellow', label: '⏰ Paid Late' },
  upcoming: { cls: 'badge-blue', label: '📅 Upcoming' },
  overdue_with_late_fee: { cls: 'badge-red', label: '🔴 Late Fee Accruing' }
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || { cls: 'badge-gray', label: status };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

// WhatsApp reminder modal
function WAModal({ record, onClose }) {
  const [waMsg, setWaMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.post(`/retainer/notify/${record.leadId}`, { installmentMonth: record.overdueCount, channel: 'WhatsApp' })
      .then(r => setWaMsg(r.data.waMessage))
      .finally(() => setLoading(false));
  }, [record.leadId, record.overdueCount]);

  const openWA = () => {
    window.open(`https://wa.me/91${record.mobile}?text=${encodeURIComponent(waMsg)}`, '_blank');
    toast.success('WhatsApp opened');
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">📱 Retainer Reminder — {record.leadName}</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {loading ? <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" style={{ margin: '0 auto' }} /></div> : (
            <>
              <div className="form-group">
                <label className="form-label">WhatsApp Message (Editable)</label>
                <textarea className="form-control" rows={10} value={waMsg} onChange={e => setWaMsg(e.target.value)} style={{ fontFamily: 'inherit', fontSize: 13, lineHeight: 1.7 }} />
              </div>
              <div style={{ padding: '10px 12px', background: '#f0fdf4', borderRadius: 8, fontSize: 12, color: '#065f46', marginBottom: 8 }}>
                📤 Message will open directly in WhatsApp for <strong>{record.mobile}</strong>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-whatsapp" onClick={openWA} disabled={loading}>
            <MessageCircle size={14} /> Open in WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}

// Statement modal
function StatementModal({ leadId, leadName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/retainer/statement/${leadId}`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [leadId]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div className="modal-title">📋 Retainer Statement — {leadName}</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {loading ? <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" style={{ margin: '0 auto' }} /></div> : data && (
            <>
              {/* Summary */}
              <div className="grid grid-3 mb-16">
                <div style={{ textAlign: 'center', padding: '10px 8px', background: 'var(--surface)', borderRadius: 8 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--success)' }}>₹{(data.summary.totalPaid).toLocaleString('en-IN')}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total Paid</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px 8px', background: data.summary.totalDue > 0 ? '#fef2f2' : 'var(--surface)', borderRadius: 8 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: data.summary.totalDue > 0 ? 'var(--danger)' : 'var(--text-main)' }}>₹{(data.summary.totalDue).toLocaleString('en-IN')}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total Due</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px 8px', background: data.summary.totalLateFee > 0 ? '#fff7ed' : 'var(--surface)', borderRadius: 8 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: data.summary.totalLateFee > 0 ? 'var(--warning)' : 'var(--text-main)' }}>₹{(data.summary.totalLateFee).toLocaleString('en-IN')}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Late Fees</div>
                </div>
              </div>

              {/* Installment table */}
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Month</th><th>Due Date</th><th>Amount</th><th>Late Fee</th><th>Total Due</th><th>Status</th><th>Paid On</th><th>Days Late</th></tr>
                  </thead>
                  <tbody>
                    {data.installments.map(inst => (
                      <tr key={inst.month}>
                        <td style={{ fontSize: 13, fontWeight: 600 }}>Month {inst.month}</td>
                        <td style={{ fontSize: 12 }}>{inst.dueDate}</td>
                        <td style={{ fontSize: 13 }}>{fmt(inst.amount)}</td>
                        <td style={{ fontSize: 13, color: inst.lateFee > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
                          {inst.lateFee > 0 ? fmt(inst.lateFee) : '—'}
                        </td>
                        <td style={{ fontSize: 13, fontWeight: inst.totalDue > 0 ? 700 : 400, color: inst.totalDue > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
                          {inst.totalDue > 0 ? fmt(inst.totalDue) : '—'}
                        </td>
                        <td><StatusBadge status={inst.status} /></td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inst.paidDate || '—'}</td>
                        <td style={{ fontSize: 12, color: inst.daysDelayed > 0 ? 'var(--warning)' : 'var(--text-muted)', fontWeight: inst.daysDelayed > 0 ? 600 : 400 }}>
                          {inst.daysDelayed > 0 ? `${inst.daysDelayed} days late` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Disclaimer */}
              <div style={{ marginTop: 16, padding: '10px 12px', background: '#fff7ed', borderRadius: 8, fontSize: 11, color: '#92400e', lineHeight: 1.6 }}>
                <strong>Note:</strong> Late fee of ₹100/day applies after 5-day grace period from due date. All payments subject to Easy Settlement terms and conditions.
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// Main Retainer Page
export default function RetainerPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [waRecord, setWaRecord] = useState(null);
  const [statementLead, setStatementLead] = useState(null);
  const [paymentLinkClient, setPaymentLinkClient] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = () => {
    setLoading(true);
    api.get('/retainer').then(r => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleExpand = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const filtered = data?.records?.filter(r => {
    if (filter === 'overdue') return r.status === 'overdue';
    if (filter === 'current') return r.status === 'current';
    return true;
  }) || [];

  return (
    <div className="page">
      <div className="flex-between mb-24">
        <h2 style={{ fontSize: 18 }}>Retainer Billing Engine</h2>
        <button
          className="btn"
          style={{ background: '#25d366', color: 'white', fontWeight: 600, fontSize: 13 }}
          onClick={async () => {
            if (!window.confirm('Send WhatsApp payment reminders to all overdue enrolled clients?')) return;
            try {
              const r = await api.post('/retainer/send-reminders');
              toast.success(`Reminders sent to ${r.data.sent} clients. Skipped: ${r.data.skipped}`);
            } catch (e) { toast.error('Failed to send reminders'); }
          }}
        >
          💬 Send WhatsApp Reminders
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
            { key: 'all', label: 'All' },
            { key: 'overdue', label: '⚠ Overdue' },
            { key: 'current', label: '✓ Current' },
          ].map(f => (
            <button key={f.key} className={`btn btn-sm ${filter === f.key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
      </div>

      {/* Summary cards */}
      {data?.summary && (
        <div className="grid grid-4 mb-24">
          <div className="stat-card" style={{ borderTop: '3px solid var(--navy)' }}>
            <div className="card-title">Total Enrolled</div>
            <div className="card-value">{data.summary.totalEnrolled}</div>
          </div>
          <div className="stat-card" style={{ borderTop: '3px solid var(--danger)' }}>
            <div className="card-title">Overdue Clients</div>
            <div className="card-value" style={{ color: data.summary.overdue > 0 ? 'var(--danger)' : 'var(--text-main)' }}>{data.summary.overdue}</div>
            <div className="card-sub">{fmt(data.summary.totalOverdueAmount)} pending</div>
          </div>
          <div className="stat-card" style={{ borderTop: '3px solid var(--warning)' }}>
            <div className="card-title">Total Late Fees</div>
            <div className="card-value" style={{ color: data.summary.totalLateFees > 0 ? 'var(--warning)' : 'var(--text-main)' }}>{fmt(data.summary.totalLateFees)}</div>
            <div className="card-sub">@₹100/day after 5-day grace</div>
          </div>
          <div className="stat-card" style={{ borderTop: '3px solid var(--success)' }}>
            <div className="card-title">Expected Monthly</div>
            <div className="card-value">{fmt(data.summary.expectedMonthly)}</div>
            <div className="card-sub">{data.summary.current} clients current</div>
          </div>
        </div>
      )}

      {/* Overdue banner */}
      {data?.summary?.overdue > 0 && (
        <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertCircle size={18} style={{ color: 'var(--danger)', flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: 13 }}>
            <strong>{data.summary.overdue} clients</strong> have overdue retainer payments totaling <strong>{fmt(data.summary.totalOverdueAmount)}</strong> including late fees.
          </div>
          <button className="btn btn-danger btn-sm" onClick={() => setFilter('overdue')}>View All Overdue</button>
        </div>
      )}

      {/* Client list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <div>
          {filtered.map(record => (
            <div key={record.leadId} className="card mb-12" style={{ borderLeft: `4px solid ${record.status === 'overdue' ? 'var(--danger)' : 'var(--success)'}` }}>
              <div className="flex-between">
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{record.leadName}</div>
                    <StatusBadge status={record.status} />
                    {record.overdueCount > 0 && (
                      <span className="badge badge-red">{record.overdueCount} installment{record.overdueCount > 1 ? 's' : ''} pending</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 20, fontSize: 12, color: 'var(--text-muted)' }}>
                    <span>📱 {record.mobile}</span>
                    <span>📅 Enrolled: {record.enrollmentDate}</span>
                    <span>💰 Monthly: {fmt(record.monthlyFee)}</span>
                    <span>👤 {record.assignedToName}</span>
                    {record.nextDueDate && <span style={{ color: 'var(--accent)', fontWeight: 500 }}>Next due: {record.nextDueDate}</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {record.status === 'overdue' && (
                    <div style={{ textAlign: 'right', marginRight: 8 }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--danger)' }}>{fmt(record.totalOverdue)}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>incl. {fmt(record.totalLateFee)} late fee</div>
                    </div>
                  )}
                  <button className="btn btn-whatsapp btn-sm" onClick={() => setWaRecord(record)}>
                    <MessageCircle size={13} /> Remind
                  </button>
                  <button className="btn btn-sm" style={{ background: '#10b981', color: 'white' }} onClick={() => setPaymentLinkClient({ id: record.leadId, name: record.leadName, mobile: record.leadMobile || record.mobile, monthlyFee: record.monthlyFee, enrolled: true })}>
                    🔗 Pay Link
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setStatementLead({ id: record.leadId, name: record.leadName })}>
                    <IndianRupee size={13} /> Statement
                  </button>
                  <button className="btn-icon" onClick={() => toggleExpand(record.leadId)}>
                    {expanded[record.leadId] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>
              </div>

              {/* Expanded installments */}
              {expanded[record.leadId] && record.expectedInstallments.length > 0 && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr><th>Month</th><th>Due Date</th><th>Amount</th><th>Late Fee</th><th>Days Overdue</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {record.expectedInstallments.map(inst => (
                          <tr key={inst.month}>
                            <td style={{ fontSize: 13, fontWeight: 600 }}>Month {inst.month}</td>
                            <td style={{ fontSize: 12 }}>{inst.dueDate}</td>
                            <td style={{ fontSize: 13 }}>{fmt(inst.amount)}</td>
                            <td style={{ fontSize: 13, color: inst.lateFee > 0 ? 'var(--danger)' : 'var(--text-muted)', fontWeight: inst.lateFee > 0 ? 700 : 400 }}>
                              {inst.lateFee > 0 ? fmt(inst.lateFee) : '—'}
                            </td>
                            <td style={{ fontSize: 13, color: inst.daysOverdue > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
                              {inst.daysOverdue > 0 ? `${inst.daysOverdue} days` : '—'}
                            </td>
                            <td>
                              {inst.isPaid
                                ? <span className="badge badge-green">✓ Paid</span>
                                : <span className="badge badge-red">⚠ Overdue {inst.lateFee > 0 ? `+ ₹${inst.lateFee} late fee` : ''}</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {record.totalLateFee > 0 && (
                    <div style={{ marginTop: 10, padding: '8px 12px', background: '#fff7ed', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                      ⚠️ Late fee: ₹100/day after 5-day grace period. Total accrued: <strong>{fmt(record.totalLateFee)}</strong>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="empty-state">
              <CheckCircle size={40} style={{ color: 'var(--success)' }} />
              <h3>{filter === 'overdue' ? 'No overdue clients!' : 'No clients found'}</h3>
              <p>{filter === 'overdue' ? 'All clients are current on payments.' : 'Enroll clients to see retainer billing.'}</p>
            </div>
          )}
        </div>
      )}

      {waRecord && <WAModal record={waRecord} onClose={() => setWaRecord(null)} />}
      {statementLead && <StatementModal leadId={statementLead.id} leadName={statementLead.name} onClose={() => setStatementLead(null)} />}
      {paymentLinkClient && <ClientPaymentLinkModal lead={paymentLinkClient} onClose={() => setPaymentLinkClient(null)} />}
    </div>
  );
}
